import { getErrorMessage, useServices, useToast } from '@/app/providers';
import { DataTable, type ColumnDef } from '@/core/ui/components';
import { useCrudNotifications } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { useAbility } from '@/modules/identity/application/ability';
import type { Notification, NotificationChannel, NotificationPriority, NotificationType } from '@yawi/domain';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  AlertTriangle,
  Bell,
  CheckCircle,
  Clock,
  Filter,
  Loader2,
  Mail,
  MessageSquare,
  MoreVertical,
  Phone,
  Plus,
  RefreshCw,
  Send,
  Settings,
  Smartphone,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from '@/pages/AdminPanel';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';

const PRIORITY_OPTIONS: NotificationPriority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

const getPriorityLabel = (priority: NotificationPriority, t: (key: string) => string): string => {
  const labels: Record<NotificationPriority, string> = {
    LOW: t('notification.priority.low'),
    NORMAL: t('notification.priority.normal'),
    HIGH: t('notification.priority.high'),
    URGENT: t('notification.priority.urgent'),
  };
  return labels[priority];
};

const NOTIFICATION_TYPES: { value: NotificationType; label: string }[] = [
  { value: 'ANNOUNCEMENT_INFO', label: 'Anuncio (Info)' },
  { value: 'ANNOUNCEMENT_IMPORTANT', label: 'Anuncio (Importante)' },
  { value: 'ANNOUNCEMENT_URGENT', label: 'Anuncio (Urgente)' },
  { value: 'ANNOUNCEMENT_EVENT', label: 'Evento' },
  { value: 'PAYMENT_REMINDER_5D', label: 'Recordatorio de pago (5 días)' },
  { value: 'PAYMENT_REMINDER_DUE', label: 'Recordatorio de pago (vencimiento)' },
  { value: 'PAYMENT_OVERDUE', label: 'Pago vencido' },
  { value: 'RESERVATION_REMINDER_24H', label: 'Recordatorio reserva (24h)' },
  { value: 'CUSTOM', label: 'Personalizada' },
];

const CHANNELS: { value: NotificationChannel; label: string; icon: React.ReactNode }[] = [
  { value: 'IN_APP', label: 'App', icon: <Smartphone size={14} /> },
  { value: 'PUSH', label: 'Push', icon: <Bell size={14} /> },
  { value: 'EMAIL', label: 'Email', icon: <Mail size={14} /> },
  { value: 'SMS', label: 'SMS', icon: <Phone size={14} /> },
  { value: 'WHATSAPP', label: 'WhatsApp', icon: <MessageSquare size={14} /> },
];

const getPriorityColor = (priority: NotificationPriority): string => {
  switch (priority) {
    case 'URGENT':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'HIGH':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'NORMAL':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'LOW':
    default:
      return 'bg-slate-100 text-slate-600 border-slate-200';
  }
};

const getChannelIcon = (channel: NotificationChannel): React.ReactNode => {
  const found = CHANNELS.find((c) => c.value === channel);
  return found?.icon || <Bell size={14} />;
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'READ':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle size={12} /> Leída
        </span>
      );
    case 'DELIVERED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <CheckCircle size={12} /> Entregada
        </span>
      );
    case 'SENT':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
          <Send size={12} /> Enviada
        </span>
      );
    case 'PENDING':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <Clock size={12} /> Pendiente
        </span>
      );
    case 'FAILED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <AlertTriangle size={12} /> Fallida
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
          {status}
        </span>
      );
  }
};

const formatTime = (dateString: string): string => {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: es });
  } catch {
    return dateString;
  }
};

export const AdminNotificationList: React.FC = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { notificationRepo, userRepo } = useServices();
  const ability = useAbility();
  const canCreateNotification = ability.can('manage', 'all') || ability.can('create', 'Notification');
  const { withCreateNotification } = useCrudNotifications({ entityName: 'Notificación' });

  // Notifications list state (manual since no pagination in repo)
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<NotificationChannel | ''>('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Form state for sending notification
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [notificationType, setNotificationType] = useState<NotificationType>('CUSTOM');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<NotificationPriority>('NORMAL');
  const [selectedChannels, setSelectedChannels] = useState<NotificationChannel[]>(['IN_APP', 'PUSH']);

  // Users for selection
  const [users, setUsers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Debounce the search term so we don't hit the API on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // El selector busca en el servidor: cargar 100 usuarios y filtrar en memoria
  // dejaba sin resultado a cualquier destinatario fuera de esa primera página.
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    if (!isModalOpen) return;

    let active = true;
    setLoadingUsers(true);
    userRepo
      .getUsers({ limit: 20, search: debouncedUserSearch || undefined })
      .then((result) => {
        if (!active) return;
        setUsers(result.data.map((u) => ({ id: u.id, name: u.name, email: u.email })));
      })
      .catch(console.error)
      .finally(() => {
        if (active) setLoadingUsers(false);
      });

    return () => {
      active = false;
    };
  }, [isModalOpen, userRepo, debouncedUserSearch]);

  // Fetch notifications (server-side search + channel filter). Solo la última
  // petición escribe el estado: una respuesta lenta no pisa la búsqueda actual.
  const requestIdRef = useRef(0);
  const fetchNotifications = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = await notificationRepo.getNotifications({
        limit: 50,
        search: debouncedSearch || undefined,
        channel: channelFilter || undefined,
      });
      if (requestId !== requestIdRef.current) return;
      setNotifications(response.data);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      toast.error(getErrorMessage(err), 'Error al cargar notificaciones');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [notificationRepo, channelFilter, debouncedSearch, toast]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  // Load users when modal opens

  const resetForm = () => {
    setSelectedUsers([]);
    setNotificationType('CUSTOM');
    setTitle('');
    setBody('');
    setPriority('NORMAL');
    setSelectedChannels(['IN_APP', 'PUSH']);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateNotification) return;
    if (!title || !body || selectedUsers.length === 0) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    setIsSending(true);
    try {
      if (selectedUsers.length > 1) {
        await withCreateNotification(() =>
          notificationRepo.sendBulkNotification({
            userIds: selectedUsers,
            type: notificationType,
            title,
            body,
            priority,
            channels: selectedChannels,
          })
        );
      } else {
        await withCreateNotification(() =>
          notificationRepo.sendNotification({
            userId: selectedUsers[0],
            type: notificationType,
            title,
            body,
            priority,
            channels: selectedChannels,
          })
        );
      }
      setIsModalOpen(false);
      resetForm();
      fetchNotifications();
    } catch {
      // Error handled by hook
    } finally {
      setIsSending(false);
    }
  };

  const toggleChannel = (channel: NotificationChannel) => {
    if (channel === 'IN_APP') return; // IN_APP always required
    setSelectedChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    );
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u.id));
    }
  };

  const columns: ColumnDef<Notification>[] = useMemo(
    () => [
      {
        id: 'content',
        header: 'Notificación',
        cell: (notif) => (
          <div className="max-w-xs">
            <p className="font-semibold text-slate-800 truncate">{notif.title}</p>
            <p className="text-xs text-slate-500 line-clamp-2">{notif.body}</p>
          </div>
        ),
      },
      {
        id: 'type',
        header: 'Tipo',
        cell: (notif) => {
          const found = NOTIFICATION_TYPES.find((t) => t.value === notif.type);
          return (
            <span className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
              {found?.label || notif.type}
            </span>
          );
        },
      },
      {
        id: 'channel',
        header: 'Canal',
        cell: (notif) => (
          <span className="flex items-center gap-1 flex-wrap">
            {notif.deliveries.map((d) => (
              <span key={d.channel} className="flex items-center gap-1 text-slate-600">
                {getChannelIcon(d.channel)}
                <span className="text-sm">{CHANNELS.find((c) => c.value === d.channel)?.label || d.channel}</span>
              </span>
            ))}
          </span>
        ),
      },
      {
        id: 'priority',
        header: 'Prioridad',
        cell: (notif) => (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(notif.priority)}`}
          >
            {getPriorityLabel(notif.priority, t)}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Estado',
        cell: (notif) => getStatusBadge(notif.status),
      },
      {
        id: 'date',
        header: 'Fecha',
        cell: (notif) => (
          <span className="text-sm text-slate-500">{formatTime(notif.createdAt)}</span>
        ),
      },
    ],
    []
  );

  return (
    <>
      <DataTable
        data={notifications}
        columns={columns}
        keyExtractor={(notif) => notif.id}
        meta={null}
        onPageChange={() => {}}
        title={t('notifications.title')}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar notificación..."
        loading={loading}
        emptyMessage="No hay notificaciones registradas."
        actions={
          <div className="flex items-center gap-3">
            {/* Refresh button */}
            <Button variant="secondary" onClick={fetchNotifications} icon={<RefreshCw size={16} />}>
              Actualizar
            </Button>

            {/* Settings link */}
            <Link
              to={ADMIN_ROUTES['notificaciones-config']}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors"
            >
              <Settings size={16} />
              Configuración
            </Link>

            {/* Channel filter */}
            <div className="relative">
              <select
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value as NotificationChannel | '')}
                className="pl-8 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Todos los canales</option>
                {CHANNELS.map((channel) => (
                  <option key={channel.value} value={channel.value}>
                    {channel.label}
                  </option>
                ))}
              </select>
              <Filter size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {canCreateNotification && (
              <Button onClick={() => setIsModalOpen(true)} icon={<Plus size={16} />}>
                {t('notifications.send')}
              </Button>
            )}
          </div>
        }
        rowActions={(notif) => (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(openMenuId === notif.id ? null : notif.id);
              }}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <MoreVertical size={18} />
            </button>

            {openMenuId === notif.id && (
              <div
                className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    setOpenMenuId(null);
                    toast.info('Funcionalidad de reenvío próximamente');
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Send size={16} className="text-blue-500" /> Reenviar
                </button>
              </div>
            )}
          </div>
        )}
      />

      {/* Send Notification Modal */}
      {isModalOpen && canCreateNotification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
              <h3 className="font-bold text-slate-800 text-lg">
                {t('notifications.send')}
              </h3>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
              >
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            <form onSubmit={handleSend} className="p-6 space-y-5 overflow-y-auto custom-scrollbar">
              {/* Recipients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Destinatarios *
                  </label>
                  <button
                    type="button"
                    onClick={selectAllUsers}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    {selectedUsers.length === users.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Buscar usuario..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 size={20} className="animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                    {users.map((user) => (
                      <label
                        key={user.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          selectedUsers.includes(user.id) ? 'bg-primary-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUser(user.id)}
                          className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-slate-700">{user.name}</span>
                        <span className="text-xs text-slate-400">{user.email}</span>
                      </label>
                    ))}
                  </div>
                )}
                {selectedUsers.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedUsers.length} destinatario(s) seleccionado(s)
                  </p>
                )}
              </div>

              {/* Notification Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Tipo de notificación
                </label>
                <select
                  value={notificationType}
                  onChange={(e) => setNotificationType(e.target.value as NotificationType)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  {NOTIFICATION_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <Input
                label="Título *"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej. Recordatorio de pago"
                required
              />

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Mensaje *
                </label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                  rows={3}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Escribe el mensaje aquí..."
                  required
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Prioridad
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {PRIORITY_OPTIONS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      className={`py-2 text-xs font-bold rounded-lg border transition-all ${
                        priority === p
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {getPriorityLabel(p, t)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Channels */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Canales de envío
                </label>
                <div className="flex flex-wrap gap-2">
                  {CHANNELS.map((channel) => (
                    <button
                      key={channel.value}
                      type="button"
                      onClick={() => toggleChannel(channel.value)}
                      disabled={channel.value === 'IN_APP'}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                        selectedChannels.includes(channel.value)
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                      } ${channel.value === 'IN_APP' ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {channel.icon}
                      {channel.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  icon={isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  disabled={isSending || selectedUsers.length === 0 || !title || !body}
                >
                  {isSending ? 'Enviando...' : `Enviar (${selectedUsers.length})`}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
